/**
 * Currency switching for the pricing module.
 *
 * Both currencies are rendered into the markup (data-price-usd / data-price-cad)
 * and the visitor's one is picked in the browser. The pages are cached
 * server-side (NXCMS_DEFAULT_CACHE), so the currency cannot be resolved during
 * rendering without serving one visitor's currency to everyone else.
 *
 * Resolution order:
 *   1. ?currency=CAD in the URL (lets us link/QA a currency explicitly)
 *   2. the visitor's own earlier choice (localStorage)
 *   3. a same-origin geo endpoint, if one is configured (window.CTM_GEO_URL)
 *   4. IANA time zone / browser locale heuristic
 *   5. USD
 *
 * The third-party IP lookup APIs are blocked by our CSP (connect-src 'self'),
 * hence step 3 expects our own endpoint returning {"country": "CA"}.
 */

const STORAGE_KEY = "ctm-currency";
const DEFAULT_CURRENCY = "USD";
const SUPPORTED = ["USD", "CAD"];

const CURRENCY_NAMES = {
  USD: "US dollars",
  CAD: "Canadian dollars",
};

// Canada-only zones; the shared US/Canada ones are deliberately left out.
const CAD_TIME_ZONES = [
  "America/St_Johns",
  "America/Halifax",
  "America/Glace_Bay",
  "America/Moncton",
  "America/Goose_Bay",
  "America/Blanc-Sablon",
  "America/Toronto",
  "America/Nipigon",
  "America/Thunder_Bay",
  "America/Iqaluit",
  "America/Pangnirtung",
  "America/Atikokan",
  "America/Winnipeg",
  "America/Rainy_River",
  "America/Resolute",
  "America/Rankin_Inlet",
  "America/Regina",
  "America/Swift_Current",
  "America/Edmonton",
  "America/Cambridge_Bay",
  "America/Inuvik",
  "America/Creston",
  "America/Dawson_Creek",
  "America/Fort_Nelson",
  "America/Whitehorse",
  "America/Dawson",
  "America/Vancouver",
  "Canada/Atlantic",
  "Canada/Central",
  "Canada/Eastern",
  "Canada/Mountain",
  "Canada/Newfoundland",
  "Canada/Pacific",
  "Canada/Saskatchewan",
];

const normalize = (value) => {
  const upper = String(value || "").toUpperCase();
  return SUPPORTED.includes(upper) ? upper : null;
};

const currencyForCountry = (country) =>
  String(country || "").toUpperCase() === "CA" ? "CAD" : "USD";

const readStored = () => {
  try {
    return normalize(window.localStorage.getItem(STORAGE_KEY));
  } catch (e) {
    return null;
  }
};

const writeStored = (currency) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, currency);
  } catch (e) {
    /* private mode / storage disabled — the switch still works for this view */
  }
};

const fromQueryString = () => {
  try {
    return normalize(new URLSearchParams(window.location.search).get("currency"));
  } catch (e) {
    return null;
  }
};

const fromBrowserLocale = () => {
  let zone = null;
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    zone = null;
  }

  if (zone && CAD_TIME_ZONES.indexOf(zone) !== -1) {
    return "CAD";
  }

  const languages = navigator.languages || [navigator.language || ""];
  if (languages.some((lang) => /-CA$/i.test(lang || ""))) {
    return "CAD";
  }

  return null;
};

const fromGeoEndpoint = () => {
  const url = window.CTM_GEO_URL;
  if (!url || typeof window.fetch !== "function") {
    return Promise.resolve(null);
  }

  return window
    .fetch(url, { credentials: "omit" })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => (data ? currencyForCountry(data.country) : null))
    .catch(() => null);
};

const render = (root, currency) => {
  root.querySelectorAll("[data-price]").forEach((el) => {
    const value = el.getAttribute("data-price-" + currency.toLowerCase());
    if (value) {
      el.textContent = value;
    }
  });

  root.querySelectorAll("[data-currency-name]").forEach((el) => {
    el.textContent = CURRENCY_NAMES[currency];
  });

  root.querySelectorAll("[data-currency-set]").forEach((btn) => {
    const pressed = normalize(btn.getAttribute("data-currency-set")) === currency;
    btn.setAttribute("aria-pressed", pressed ? "true" : "false");
  });

  document.documentElement.setAttribute("data-currency", currency);
};

const init = () => {
  const root = document.querySelector(".planPricing");
  if (!root) {
    return;
  }

  // Explicit choices win and are never overwritten by detection.
  const explicit = fromQueryString() || readStored();
  let current = explicit || fromBrowserLocale() || DEFAULT_CURRENCY;
  render(root, current);

  if (!explicit) {
    fromGeoEndpoint().then((detected) => {
      // A choice made while the lookup was in flight still wins.
      if (detected && detected !== current && !readStored()) {
        current = detected;
        render(root, current);
      }
    });
  }

  root.querySelectorAll("[data-currency-set]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const picked = normalize(btn.getAttribute("data-currency-set"));
      if (!picked || picked === current) {
        return;
      }
      current = picked;
      writeStored(picked);
      render(root, picked);
    });
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
